/**
 * <countdown-timer> — Cuenta regresiva autónoma sin dependencias.
 *
 * Atributos:
 *   data-mode        "daily" | "fixed" (default: "fixed")
 *   data-timezone    Zona horaria IANA para modo daily (default: "America/Santiago")
 *   data-end-date    ISO 8601 con offset, ej: "2026-12-31T23:59:00-04:00" (solo modo fixed)
 *
 * Estructura esperada dentro del elemento:
 *   [data-unit="days"] .countdown-value    (solo en modo fixed)
 *   [data-unit="hours"] .countdown-value
 *   [data-unit="minutes"] .countdown-value
 *   [data-unit="seconds"] .countdown-value
 *   [data-countdown-message]  (oculto hasta expirar; solo modo fixed)
 *   [data-countdown-grid]     (los números; se oculta al expirar en modo fixed)
 */
if (!customElements.get('countdown-timer')) {
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    this.mode = this.getAttribute('data-mode') || 'fixed';
    this.tz = this.getAttribute('data-timezone') || 'America/Santiago';

    this.valueEls = {
      days: this.querySelector('[data-unit="days"] .countdown-value'),
      hours: this.querySelector('[data-unit="hours"] .countdown-value'),
      minutes: this.querySelector('[data-unit="minutes"] .countdown-value'),
      seconds: this.querySelector('[data-unit="seconds"] .countdown-value'),
    };
    this.gridEl = this.querySelector('[data-countdown-grid]');
    this.messageEl = this.querySelector('[data-countdown-message]');

    if (this.mode === 'daily') {
      try {
        this.tzFormatter = new Intl.DateTimeFormat('en-GB', {
          timeZone: this.tz,
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hourCycle: 'h23',
        });
      } catch (e) {
        return;
      }
      this.tick();
      this.intervalId = window.setInterval(() => this.tick(), 1000);
      return;
    }

    // Modo fixed
    const raw = this.getAttribute('data-end-date');
    this.endTime = raw ? new Date(raw).getTime() : NaN;

    if (Number.isNaN(this.endTime)) {
      return;
    }

    this.tick();
    this.intervalId = window.setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
    }
  }

  dailyRemainingSeconds() {
    const parts = this.tzFormatter.formatToParts(new Date());
    const get = (type) => parseInt(parts.find((p) => p.type === type).value, 10);
    const h = get('hour');
    const m = get('minute');
    const s = get('second');
    const elapsed = h * 3600 + m * 60 + s;
    // A medianoche exacta rebota a 86400 para el reinicio automático
    return elapsed === 0 ? 86400 : 86400 - elapsed;
  }

  tick() {
    if (this.mode === 'daily') {
      const totalSeconds = this.dailyRemainingSeconds();
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      this.setValue('hours', hours);
      this.setValue('minutes', minutes);
      this.setValue('seconds', seconds);
      return;
    }

    // Modo fixed
    const remaining = this.endTime - Date.now();

    if (remaining <= 0) {
      this.expire();
      return;
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.setValue('days', days);
    this.setValue('hours', hours);
    this.setValue('minutes', minutes);
    this.setValue('seconds', seconds);
  }

  setValue(unit, value) {
    const el = this.valueEls[unit];
    if (!el) return;
    const padded = unit === 'days' ? String(value) : String(value).padStart(2, '0');
    if (el.textContent !== padded) {
      el.textContent = padded;
    }
  }

  expire() {
    if (this.intervalId) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.gridEl) {
      this.gridEl.hidden = true;
    }
    if (this.messageEl) {
      this.messageEl.hidden = false;
    }
  }
}

customElements.define('countdown-timer', CountdownTimer);
}
